import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmptyState } from '../../../components/admin/Shared';
import { RoutePhase, RouteStopDTO } from '../../../models/APIModels';

function formatEtaWindow(start?: string, end?: string): string {
  if (start && end) return `${start}–${end}`;
  if (start) return start;
  return '';
}

interface SortableStopProps {
  stop: RouteStopDTO;
}

function SortableStop({ stop }: SortableStopProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="route-stop" {...attributes} {...listeners}>
      <div className="route-stop__position">{stop.routePosition + 1}</div>
      <div className="route-stop__main">
        <div className="route-stop__order">Order #{stop.orderId}</div>
        <div className="route-stop__meta">
          {stop.phase}
          {formatEtaWindow(stop.etaStart, stop.etaEnd) &&
            ` · ETA ${formatEtaWindow(stop.etaStart, stop.etaEnd)}`}
        </div>
      </div>
      {stop.overridden && <span className="route-stop__badge">Manually reordered</span>}
    </div>
  );
}

export interface RoutePlanGroup {
  key: string;
  driverId: number | null;
  phase: RoutePhase;
  items: RouteStopDTO[];
}

interface RoutePlanListProps {
  partnerId: number | null;
  loading: boolean;
  groups: RoutePlanGroup[];
  busy: boolean;
  driverName: (id: number | null) => string;
  onReorder: (groupDriverId: number | null, groupPhase: RoutePhase, ordered: RouteStopDTO[]) => void;
  onRecomputeGroup: (groupDriverId: number, groupPhase: RoutePhase) => void;
}

/**
 * The list-shaped Route Plan view, extracted from `RoutePlanTab.tsx` so the
 * tab can switch between Map and List sub-tabs without duplicating logic.
 *
 * Drag-and-drop reorder is preserved verbatim (per `@dnd-kit/sortable`).
 * Optimistic UI / error reverting still lives in the parent (`RoutePlanTab`)
 * — this component is the presentation half.
 */
export default function RoutePlanList({
  partnerId,
  loading,
  groups,
  busy,
  driverName,
  onReorder,
  onRecomputeGroup,
}: RoutePlanListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (groupDriverId: number | null, groupPhase: RoutePhase, items: RouteStopDTO[]) =>
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = items.findIndex((s) => s.id === active.id);
      const newIndex = items.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(items, oldIndex, newIndex);
      onReorder(groupDriverId, groupPhase, reordered);
    };

  if (partnerId == null) {
    return (
      <EmptyState
        variant="select-prerequisite"
        title="Select a partner"
        body="Pick a partner from the picker above to load the route plan."
      />
    );
  }
  if (loading) {
    return <EmptyState title="Loading route…" />;
  }
  if (groups.length === 0) {
    return <EmptyState title="No stops for this date." />;
  }

  return (
    <>
      {groups.map((g) => (
        <div key={g.key} className="route-group">
          <div className="route-group__header">
            <h4>
              {driverName(g.driverId)} · {g.phase}
            </h4>
            {g.driverId != null && (
              <button
                type="button"
                className="route-group__recompute"
                onClick={() => onRecomputeGroup(g.driverId!, g.phase)}
                disabled={busy}
              >
                Recompute group
              </button>
            )}
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd(g.driverId, g.phase, g.items)}
          >
            <SortableContext
              items={g.items.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="route-group__list">
                {g.items.map((stop) => (
                  <SortableStop key={stop.id} stop={stop} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ))}
    </>
  );
}
